import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Copy, Check, QrCode, Building2, CreditCard, FileText, Info } from 'lucide-react'
import { api } from '@/lib/api'
import { haptic } from '@/lib/telegram'
import { useStoreTheme } from '@/lib/useStoreTheme'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'

export interface FopRequisitesModalProps {
  isOpen: boolean
  onClose: () => void
  orderId?: number
  amountUah?: number
  purpose?: string
}

interface RequisitesData {
  enabled: boolean
  order_id?: number
  amount_uah?: number
  bank_recipient?: string
  bank_card?: string
  bank_iban?: string
  bank_extra_info?: string
  edrpou?: string
  purpose?: string
  nbu_payload?: string
  qr_svg?: string
}

export function FopRequisitesModal({
  isOpen,
  onClose,
  orderId,
  amountUah,
  purpose: customPurpose,
}: FopRequisitesModalProps) {
  const theme = useStoreTheme()
  const { lang } = useAppStore()
  const T = useT('fopModal')
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  // Query order-specific or generic shop requisites
  const { data, isLoading } = useQuery<RequisitesData>({
    queryKey: orderId ? ['iban-payment', orderId] : ['payment-requisites'],
    queryFn: () =>
      orderId
        ? api.get<RequisitesData>(`/orders/${orderId}/iban-payment`)
        : api.get<RequisitesData>('/payment/requisites'),
    enabled: isOpen,
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const recipient = data?.bank_recipient || ''
  const iban = data?.bank_iban || ''
  const card = data?.bank_card || ''
  const edrpou = data?.edrpou || ''
  const extraInfo = data?.bank_extra_info || ''
  const orderAmount = data?.amount_uah ?? amountUah
  const effectivePurpose =
    customPurpose ||
    data?.purpose ||
    (orderId ? `Замовлення #${orderId}` : 'Оплата замовлення згідно рахунку')

  const copyToClipboard = (text: string, key: string) => {
    void navigator.clipboard.writeText(text)
    haptic('success')
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const allRequisitesText = [
    recipient ? `Отримувач: ${recipient}` : '',
    edrpou ? `ЄДРПОУ / ІПН: ${edrpou}` : '',
    iban ? `IBAN: ${iban}` : '',
    card ? `Картка: ${card}` : '',
    orderAmount ? `Сума: ${orderAmount} UAH` : '',
    effectivePurpose ? `Призначення: ${effectivePurpose}` : '',
    extraInfo ? `Інформація: ${extraInfo}` : '',
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-200">
      {/* Backdrop click */}
      <div className="fixed inset-0" onClick={onClose} />

      <div
        className={`relative z-10 w-full max-w-lg max-h-[92vh] flex flex-col rounded-3xl border shadow-2xl overflow-hidden transition-all ${
          theme.isDarkTheme
            ? 'bg-[#1e2329] border-[#3a4352] text-slate-100'
            : 'bg-white border-slate-200 text-slate-900'
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${
            theme.isDarkTheme
              ? 'border-[#2d3748] bg-[#242c38]'
              : 'border-slate-100 bg-slate-50/80'
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div
              className={`p-2 rounded-xl ${
                theme.isDarkTheme
                  ? 'bg-amber-500/20 text-[#c9a24b]'
                  : 'bg-indigo-50 text-indigo-600'
              }`}
            >
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight">
                {T.title}
              </h2>
              <p className="text-[11px] text-slate-400">
                {T.subtitle}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-full transition ${
              theme.isDarkTheme
                ? 'text-slate-400 hover:text-white hover:bg-white/10'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-4">
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-center">
              <div
                className={`h-8 w-8 animate-spin rounded-full border-2 border-t-transparent ${
                  theme.isDarkTheme ? 'border-amber-400' : 'border-indigo-600'
                }`}
              />
              <p className="mt-3 text-xs text-slate-400">
                {T.loading}
              </p>
            </div>
          ) : !data?.enabled && !iban && !card ? (
            <div className="py-8 text-center text-sm text-slate-400">
              <Info className="h-8 w-8 mx-auto mb-2 text-slate-500" />
              <p>
                {T.notConfigured}
              </p>
            </div>
          ) : (
            <>
              {/* NBU QR Code (if present) */}
              {data?.qr_svg && (
                <div
                  className={`flex flex-col items-center justify-center p-4 rounded-2xl border ${
                    theme.isDarkTheme
                      ? 'bg-white border-[#3a4352]'
                      : 'bg-white border-slate-200 shadow-inner'
                  }`}
                >
                  <div
                    className="w-40 h-40 shrink-0"
                    dangerouslySetInnerHTML={{ __html: data.qr_svg }}
                  />
                  <div className="mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                    <QrCode className="h-4 w-4 text-[#c9a24b]" />
                    <span>
                      {T.scanQrHint}
                    </span>
                  </div>
                </div>
              )}

              {/* Requisites Fields with Individual Copy Buttons */}
              <div className="space-y-2.5 font-mono text-xs">
                {/* IBAN */}
                {iban && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        IBAN рахунок
                      </span>
                      <span
                        className={`text-sm sm:text-base font-bold tracking-tight select-all block break-all ${
                          theme.isDarkTheme ? 'text-amber-300' : 'text-indigo-900'
                        }`}
                      >
                        {iban}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(iban, 'iban')}
                      className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-sans font-semibold transition shrink-0 shadow-xs ${
                        theme.isDarkTheme
                          ? 'bg-[#c9a24b] text-[#17120f] hover:bg-[#d8b25c]'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      {copiedKey === 'iban' ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати IBAN</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Recipient */}
                {recipient && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        {T.recipient}
                      </span>
                      <span
                        className={`font-semibold block truncate select-all ${
                          theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'
                        }`}
                      >
                        {recipient}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(recipient, 'recipient')}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-sans font-medium transition shrink-0 ${
                        theme.isDarkTheme
                          ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                          : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {copiedKey === 'recipient' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* EDRPOU / IPN */}
                {edrpou && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        Код ЄДРПОУ / ІПН (РНОКПП)
                      </span>
                      <span
                        className={`font-bold block select-all ${
                          theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'
                        }`}
                      >
                        {edrpou}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(edrpou, 'edrpou')}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-sans font-medium transition shrink-0 ${
                        theme.isDarkTheme
                          ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                          : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {copiedKey === 'edrpou' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Amount (if present) */}
                {typeof orderAmount === 'number' && orderAmount > 0 && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        {T.amount}
                      </span>
                      <span className="text-sm font-bold text-[#c9a24b]">
                        {orderAmount} UAH
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(String(orderAmount), 'amount')}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-sans font-medium transition shrink-0 ${
                        theme.isDarkTheme
                          ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                          : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {copiedKey === 'amount' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Purpose */}
                {effectivePurpose && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        {T.purpose}
                      </span>
                      <span
                        className={`font-semibold block truncate select-all ${
                          theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'
                        }`}
                      >
                        {effectivePurpose}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(effectivePurpose, 'purpose')}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-sans font-medium transition shrink-0 ${
                        theme.isDarkTheme
                          ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                          : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {copiedKey === 'purpose' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Card number (if exists) */}
                {card && (
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between gap-2.5 transition ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38] border-[#3a4352]'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-0.5">
                        {T.bankCard}
                      </span>
                      <span
                        className={`font-bold block select-all ${
                          theme.isDarkTheme ? 'text-slate-100' : 'text-slate-900'
                        }`}
                      >
                        {card}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(card, 'card')}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-sans font-medium transition shrink-0 ${
                        theme.isDarkTheme
                          ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30'
                          : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                      }`}
                    >
                      {copiedKey === 'card' ? (
                        <>
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Скопійовано</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          <span>Копіювати</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Extra info / Bank details */}
                {extraInfo && (
                  <div
                    className={`p-3 rounded-2xl border text-xs ${
                      theme.isDarkTheme
                        ? 'bg-[#242c38]/70 border-[#3a4352] text-slate-300'
                        : 'bg-slate-50 border-slate-200 text-slate-600'
                    }`}
                  >
                    <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 block mb-1">
                      {T.extraInfo}
                    </span>
                    <p className="font-sans whitespace-pre-wrap">{extraInfo}</p>
                  </div>
                )}
              </div>

              {/* Copy All Button */}
              <button
                type="button"
                onClick={() => copyToClipboard(allRequisitesText, 'all')}
                className={`w-full py-3 text-xs font-semibold rounded-2xl transition flex items-center justify-center gap-2 shadow-sm ${
                  theme.isDarkTheme
                    ? 'bg-[#2d3748] hover:bg-[#3a4352] text-slate-100'
                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {copiedKey === 'all' ? (
                  <>
                    <Check className="h-4 w-4 text-emerald-400" />
                    <span>
                      {lang === 'en'
                        ? 'All requisites copied to clipboard!'
                        : 'Усі реквізити скопійовано!'}
                    </span>
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 text-[#c9a24b]" />
                    <span>
                      {lang === 'en'
                        ? 'Copy all requisites at once'
                        : 'Скопіювати всі реквізити в 1 клік'}
                    </span>
                  </>
                )}
              </button>

              {/* Step-by-step Bank App Guide */}
              <div
                className={`p-3.5 rounded-2xl border text-xs space-y-2 ${
                  theme.isDarkTheme
                    ? 'bg-amber-500/5 border-amber-500/20 text-slate-300'
                    : 'bg-amber-50/70 border-amber-200 text-amber-950'
                }`}
              >
                <div className="flex items-center gap-1.5 font-bold text-[#c9a24b]">
                  <CreditCard className="h-4 w-4" />
                  <span>
                    {lang === 'en'
                      ? 'How to pay in your banking app:'
                      : 'Як оплатити через Monobank, Приват24 тощо:'}
                  </span>
                </div>
                <ol className="list-decimal list-inside space-y-1 text-[11px] leading-relaxed opacity-90">
                  <li>
                    {lang === 'en'
                      ? 'Open banking app ➔ "Payments" ➔ "By IBAN / Requisites"'
                      : 'Відкрийте банкінг ➔ «Платежі» ➔ «За IBAN / реквізитами» або «ФОП»'}
                  </li>
                  <li>
                    {lang === 'en'
                      ? 'Paste the copied IBAN — recipient details will populate automatically'
                      : 'Вставте скопійований IBAN — дані ФОП підтягнуться автоматично'}
                  </li>
                  <li>
                    {lang === 'en'
                      ? 'Enter amount and payment purpose, then confirm payment'
                      : 'Вкажіть суму та призначення платежу, після чого підтвердіть платіж'}
                  </li>
                </ol>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`p-3.5 sm:p-4 border-t flex justify-end shrink-0 ${
            theme.isDarkTheme ? 'border-[#2d3748] bg-[#1a202c]' : 'border-slate-100 bg-slate-50'
          }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`w-full sm:w-auto px-6 py-2.5 rounded-xl text-xs font-bold transition ${
              theme.isDarkTheme
                ? 'bg-[#242c38] hover:bg-[#2d3748] text-slate-200 border border-[#3a4352]'
                : 'bg-slate-200 hover:bg-slate-300 text-slate-800'
            }`}
          >
            {lang === 'en' ? 'Done' : 'Зрозуміло'}
          </button>
        </div>
      </div>
    </div>
  )
}
